import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/realtime";
import { prefillFromRecord, shortDate } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function ServiceDue() {
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get("/service-due", { params: { month, year } }).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  }, [month, year]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load);

  const createInvoice = async (row) => {
    setOpening(row.record_id);
    try {
      const path = `/databases/${row.db_id}/tables/${encodeURIComponent(row.table)}`;
      const [db, rec, settings] = await Promise.all([
        api.get(`/databases/${row.db_id}`), api.get(`${path}/records/${row.record_id}/related`), api.get("/invoice-settings"),
      ]);
      const columns = db.data.tables.find((t) => t.name === row.table).columns;
      const prefill = prefillFromRecord(rec.data.record, columns, row.db_id, row.table, settings.data);
      const due = new Date(year, month - 1, 1);
      const pad = (n) => String(n).padStart(2, "0");
      const t = new Date();
      const d = due > t ? due : (t.getFullYear() === year && t.getMonth() + 1 === month ? t : due);
      prefill.number = "";
      prefill.service_date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
      prefill.parts = 0;
      navigate("/invoices/new", { state: { prefill } });
    } catch (e) {
      toast.error(formatError(e));
      setOpening(null);
    }
  };

  const due = data?.rows.filter((r) => !r.invoice).length ?? 0;
  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Schedule</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Service due</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="service-due-summary">
            {data ? `${data.rows.length} facilities marked for ${MONTHS[month - 1]} · ${due} still to invoice` : "Loading…"}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="due-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)} data-testid={`due-month-${i + 1}`}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="due-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">{[-1, 0, 1].map((d) => <SelectItem key={d} value={String(now.getFullYear() + d)}>{now.getFullYear() + d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-8 border rounded-lg bg-card overflow-x-auto" data-testid="service-due-list">
        {!data ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-primary" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>{["Facility", "City", "Phone", "Contact", "Tech", "Freq.", "Last service", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.record_id} className="border-t hover:bg-secondary/40 transition-colors" data-testid={`due-row-${i}`}>
                  <td className="px-4 py-2.5 font-medium">{r.name || "—"}<p className="text-[10px] font-mono text-muted-foreground">{r.db_name}</p></td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.city || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="px-4 py-2.5">{r.contact || "—"}</td>
                  <td className="px-4 py-2.5">{r.tech || "—"}</td>
                  <td className="px-4 py-2.5">{r.frequency || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{shortDate(r.last_service) || "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.invoice ? (
                      <Link to={`/invoices/${r.invoice.id}`} className="inline-flex items-center gap-1 text-emerald-400 hover:underline" data-testid={`due-invoiced-${i}`}>
                        <CalendarCheck size={14} /> {r.invoice.number}
                      </Link>
                    ) : (
                      <Button size="sm" variant="outline" disabled={opening === r.record_id} onClick={() => createInvoice(r)} data-testid={`due-create-invoice-${i}`}>
                        {opening === r.record_id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Invoice
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data?.rows.length === 0 && <p className="p-10 text-center text-muted-foreground" data-testid="service-due-empty">No facilities are marked for {MONTHS[month - 1]}.</p>}
      </div>
    </div>
  );
}
